import type { GraphClient } from "./graph-client";
import { odataString } from "./graph-client";

export interface SiteInfo {
  id: string;
  displayName: string;
  webUrl: string;
}

/** Sito SharePoint collegato a un team (il gruppo M365 del team). */
export function getTeamSite(graph: GraphClient, groupId: string): Promise<SiteInfo> {
  return graph.request<SiteInfo>(`/groups/${groupId}/sites/root?$select=id,displayName,webUrl`);
}

/**
 * Sito a partire dall'URL, es. https://contoso.sharepoint.com/sites/Progetti.
 * Utile quando i dati stanno in un sito diverso da quello del team.
 */
export function getSiteByUrl(graph: GraphClient, siteUrl: string): Promise<SiteInfo> {
  const u = new URL(siteUrl);
  const path = u.pathname.replace(/\/$/, "");
  const ref = path && path !== "/" ? `${u.hostname}:${path}` : u.hostname;
  return graph.request<SiteInfo>(`/sites/${ref}?$select=id,displayName,webUrl`);
}

export interface SiteUser {
  lookupId: number;
  displayName: string;
  email?: string;
}

/**
 * Risoluzione utenti per le colonne persona.
 *
 * Le colonne persona memorizzano il LookupId dell'utente nella "User Information List"
 * del sito. Graph non espone `ensureUser`: un utente che non ha mai avuto accesso al
 * sito può non essere presente. In quel caso usare l'endpoint del Worker che chiama
 * SharePoint REST `_api/web/ensureuser` (vedi workers/api).
 */
export class SiteUsers {
  private cache = new Map<number, SiteUser>();

  constructor(
    private readonly graph: GraphClient,
    private readonly siteId: string,
  ) {}

  private get path() {
    return `/sites/${this.siteId}/lists/${encodeURIComponent("User Information List")}/items`;
  }

  async byId(lookupId: number): Promise<SiteUser | undefined> {
    const hit = this.cache.get(lookupId);
    if (hit) return hit;
    try {
      const it = await this.graph.request<{ fields: { Title: string; EMail?: string } }>(
        `${this.path}/${lookupId}?$expand=fields($select=Title,EMail)`,
      );
      const u = { lookupId, displayName: it.fields.Title, email: it.fields.EMail };
      this.cache.set(lookupId, u);
      return u;
    } catch {
      return undefined;
    }
  }

  async byEmail(email: string): Promise<SiteUser | undefined> {
    for (const u of this.cache.values()) if (u.email?.toLowerCase() === email.toLowerCase()) return u;
    const page = await this.graph.getPage<{ id: string; fields: { Title: string; EMail?: string } }>(
      `${this.path}?$expand=fields($select=Title,EMail)&$filter=fields/EMail eq ${odataString(email)}&$top=1`,
      { Prefer: "HonorNonIndexedQueriesWarningMayFailRandomly" },
    );
    const it = page.items[0];
    if (!it) return undefined;
    const u = { lookupId: Number(it.id), displayName: it.fields.Title, email: it.fields.EMail };
    this.cache.set(u.lookupId, u);
    return u;
  }
}
